import { IdentityAvatar } from '@/heroui/IdentityAvatar';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Modal, Spinner, Button } from '@heroui/react';
import type { UserSearchResult } from '@bunker/shared/types';
import { api } from '@/lib/api';
import { useDataStore } from '@/stores/dataStore';
import { useFriends } from '@/lib/useFriends';
import { toast } from '@/lib/toast';
import { errToToast } from '@/lib/humanizeApiError';
import { SearchIcon, UserPlusIcon, UserCheckIcon, ClockIcon, ChatBubbleIcon } from '@/components/icons';
import { LoadError } from '@/components/ui/LoadError';

/**
 * Find ANY user by username/display name (authed /api/users/search) — the web
 * counterpart of the mobile people search, so a brand-new account with no
 * shared guild or DM can still discover people to message or friend.
 */
export function UserSearchModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const upsertDmChannel = useDataStore((s) => s.upsertDmChannel);
  const selectDirectChannel = useDataStore((s) => s.selectDirectChannel);
  const friends = useFriends();

  useEffect(() => {
    if (open) {
      setQuery('');
      setDebounced('');
    }
  }, [open]);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(id);
  }, [query]);

  const enabled = open && debounced.length >= 1;
  const search = useQuery({
    queryKey: ['user-search', debounced],
    queryFn: () => api.searchUsers(debounced),
    enabled,
    retry: 1,
  });
  const results = useMemo(() => search.data?.users ?? [], [search.data]);

  async function openDm(userId: string) {
    try {
      const channel = await api.openDirectMessage(userId);
      upsertDmChannel(channel);
      selectDirectChannel(channel.id);
      onClose();
    } catch (err) {
      // Say why (DMs closed, friends only, blocked) rather than a bare «failed».
      toast.error(...errToToast(err, t('ui2.peopleSearch.openDmFailed')));
    }
  }

  return (
    <Modal.Backdrop className="app-viewport-overlay" isOpen={open} onOpenChange={(o) => !o && onClose()}>
      <Modal.Container className="app-panel-container" size="lg" placement="top">
        <Modal.Dialog aria-label={t('ui2.peopleSearch.title')} className="app-panel-dialog app-search-dialog overflow-hidden p-0">
          <div className="app-search-header flex shrink-0 items-center gap-3 px-5 py-4">
            <SearchIcon className="h-5 w-5 shrink-0 text-ink-muted" />
            <input
              aria-label={t('ui2.peopleSearch.title')}
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('ui2.peopleSearch.placeholder')}
              className="block min-h-11 min-w-0 flex-1 bg-transparent text-base text-ink placeholder:text-ink-muted focus:outline-none"
            />
            {search.isFetching && <Spinner size="sm" />}
            <Modal.CloseTrigger className="app-search-close" />
          </div>
          <div className="border-t border-separator" />
          <div className="app-panel-results scrollbar-thin min-h-0 overflow-y-auto py-1">
            {enabled && search.isLoading && (
              <p role="status" className="px-5 py-6 text-center text-sm text-ink-muted">{t('common.loading')}</p>
            )}
            {enabled && search.isError && (
              <LoadError error={search.error} onRetry={() => void search.refetch()} />
            )}
            {enabled && !search.isLoading && !search.isError && results.length === 0 && (
              <p role="status" className="px-5 py-6 text-center text-sm text-ink-muted">
                {t('ui2.peopleSearch.empty')}
              </p>
            )}
            {enabled && !search.isError && results.map((u) => (
              <UserRow
                key={u.id}
                user={u}
                // The live friends query is authoritative after it loads.
                state={friends.isLoading ? u.friendState : friends.stateOf(u.id)}
                addPending={friends.pendingId === u.id}
                onOpenDm={() => void openDm(u.id)}
                onAddFriend={() => friends.sendRequest(u.id)}
              />
            ))}
          </div>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}

function UserRow({
  user,
  state,
  addPending,
  onOpenDm,
  onAddFriend,
}: {
  user: UserSearchResult;
  state: 'none' | 'friends' | 'incoming' | 'outgoing';
  addPending: boolean;
  onOpenDm: () => void;
  onAddFriend: () => void;
}) {
  const { t } = useTranslation();
  const name = user.displayName || user.username;

  return (
    <div className="flex items-center gap-3 px-5 py-2.5">
      <button type="button" onClick={onOpenDm} className="flex min-h-11 min-w-0 flex-1 items-center gap-3 rounded-lg text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
        <IdentityAvatar id={user.id} name={name} src={user.avatarUrl} size="sm" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-ink">{name}</span>
          <span className="block truncate text-xs text-ink-muted">@{user.username}</span>
        </span>
      </button>

      {state === 'friends' ? (
        <span className="flex items-center gap-1 text-xs font-medium text-success">
          <UserCheckIcon className="h-4 w-4" />
          {t('ui2.peopleSearch.friends')}
        </span>
      ) : state === 'outgoing' ? (
        <span className="flex items-center gap-1 text-xs text-ink-muted">
          <ClockIcon className="h-4 w-4" />
          {t('ui2.peopleSearch.requested')}
        </span>
      ) : (
        <Button
          size="sm"
          variant="ghost"
          isIconOnly
          className="min-h-11 min-w-11"
          aria-label={t('ui2.peopleSearch.addFriend')}
          isDisabled={addPending}
          onPress={onAddFriend}
        >
          <UserPlusIcon className="h-4 w-4" />
        </Button>
      )}

      <Button size="sm" variant="ghost" isIconOnly className="min-h-11 min-w-11" aria-label={t('ui2.peopleSearch.message')} onPress={onOpenDm}>
        <ChatBubbleIcon className="h-4 w-4" />
      </Button>
    </div>
  );
}
